import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection/AccordionSection";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import Button from "@insite/mobius/Button/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import TextField from "@insite/mobius/TextField/TextField";
import Typography from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { AccordionSectionPresentationProps } from "@ucs/MobiusOverrides/AccordionSection/AccordionSection";
import { AccordionSectionPanelProps } from "@ucs/MobiusOverrides/AccordionSection/AccordionSectionPanel";
import UCSColors from "@ucs/Utils/UCSColors";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps { }

type Props = OwnProps;

const enum fields {
    headerSections = "headerSections"
}


interface OwnProps extends WidgetProps {
    fields: {
        [fields.headerSections]: SectionModel[];
    };
}


interface SectionModel {
    fields: {
        title: string;
        content: string;
        padding: number;
    };
}

export interface ProductListFiltersAccordionStyles {
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const filtersAccordionStyles: ProductListFiltersAccordionStyles = {
    accordion: {
        css: css`
            padding-top: 20px;
            border-bottom: 2px solid ${UCSColors.gray08};
            && dt,
            && dd,
            && button,
            && span {
                border-color: ${UCSColors.gray08};
                border-width: 2px;
                color: ${UCSColors.gray04};
                background: transparent;
                padding-left: 0;
                font-size: 13px;
                text-transform: uppercase;
                &:focus {
                    padding-left: 0;
                    border-color: ${UCSColors.gray08};
                    border-width: 2px;
                }
            }
            && dd {
                padding-top: 0;
                margin-top: -12px;
                text-transform: capitalize;
            }
            && button {
                padding-top: 7px;
                padding-bottom: 25px;
                &:focus {
                    padding-top: 7px;
                    padding-bottom: 25px;
                }
            }
        `,
    },
    accordionSection: {
        toggleIconProps: {
            size: 32,
        },
        panelProps: {
            css: css`
            margin: 0;
            border: 1px solid ${getColor("common.border")};
            border-top: 0;
            display: block;
            max-height: 0;
            padding: 0;
            transition: max-height 250ms ease-out;
            overflow: hidden;
            ${({ hidden }: AccordionSectionHeaderProps) =>
                    hidden
                        ? css`
                  `
                        : css`
                        max-height: 500px;
                        transition: max-height 500ms ease-in;
                        `
                }
            `,
        },
        headerProps: {
            css: css`
            button {
                direction: rtl;
                padding: 15px !important;
                justify-content: left;
                > span {
                    font-size: 20px;
                    direction: ltr;
                }
                ${({ expanded }: AccordionSectionHeaderProps) =>
                    expanded
                        ? css`
                            background-color: ${getColor("primary.main")};
                            > span {
                                color: white;
                            }
                          `
                        : css`
                        background-color: ${getColor("common.background")};
                      `}
            }

            ${IconWrapper}.toggle {
                transform-origin: center;
                transition: transform 500ms ease-in-out;
                transform: rotate(-90deg);
                ${({ expanded }: AccordionSectionHeaderProps) =>
                    expanded
                        ? css`
                              transform: rotate(0deg);
                          `
                        : null}
            }`,
        }
    }
};

const styles = filtersAccordionStyles;

const AnimatedDropdown: FC<Props> = ({ id, fields }) => {
    if (fields.headerSections === undefined) {
        fields.headerSections = [];
    }
    return (
        <Accordion headingLevel={4}>
            {fields.headerSections?.map((section, index) => (
                // eslint-disable-next-line react/jsx-key
                <AccordionSection title={section.fields.title} {...styles.accordionSection}>
                    <StyledWrapper style={{ padding: section.fields.padding, background: "#FFF" }}>
                        {parse(section.fields.content, parserOptions)}
                    </StyledWrapper>
                </AccordionSection>
            ))}
        </Accordion >
    );
};

const contentTab = { displayName: "Content", sortOrder: 1 };

const widgetModule: WidgetModule = {
    component: AnimatedDropdown,
    definition: {
        group: "Common",
        displayName: "Animated Dropdown",
        fieldDefinitions: [
            {
                name: fields.headerSections,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "content",
                        displayName: "Content",
                        defaultValue: "",
                        editorTemplate: "RichTextField",
                    },
                    {
                        name: "padding",
                        displayName: "Content Padding",
                        defaultValue: 15,
                        editorTemplate: "IntegerField",
                    }
                ],
            },
        ],
    },
};

export default widgetModule;
